import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  sayi, tl, yuzde, round2, tarihTR,
  ARAC_GRUPLARI, grupKodu, PARCALAR, BOLGELER, parcaBul, etkiDuzeyi,
  ISLEMLER, ONARIM_SEVIYELERI, BOYALAR,
  maddiHasarHesapla, degerKaybiPiyasa, degerKaybiEk1, degerKaybiFormulle, mahrumiyetHesapla,
  endeksKatsayisi, isGunuEkle, yilEkle, sonucOzeti, ZMSS_MADDI_LIMIT, DAYANAKLAR, bugunISO,
  tutarYaz, ilanBaglantilari,
} from '../../utils/aracHasar';
import { aracHasarWordUret } from '../../utils/aracHasarWord';
import { documentAPI, caseFileAPI } from '../../api/api';
import { onayla } from '../../utils/onayla';
import useTsb from './useTsb';
import { AracSecimAlanlari, TsbBilgi } from './AracSecici';
import DosyaRuhsat from './DosyaRuhsat';
import './AracHasarHesap.css';

/*
  ARAÇ HASAR VE DEĞER KAYBI HESABI
  --------------------------------
  Tam sayfa (Hesaplama menüsü) ya da dosya formunda açılan panel olarak
  çalışır (İşçilik alacakları hesabıyla aynı düzen). Hesap kuralları ve
  dayanakları utils/aracHasar.js'te.

  Girilen bilgiler tarayıcıda taslak olarak saklanır (dosyaya göre ayrı);
  sayfa kapanınca kaybolmasın diye.
*/

const bugun = bugunISO;
const yeniEmsal = () => ({ kaynak: '', hasarsiz: '', hasarli: '' });

const limitYaz = (yil) => (ZMSS_MADDI_LIMIT[yil] ? tutarYaz(ZMSS_MADDI_LIMIT[yil]) : '');

const bosForm = (cf = {}) => {
  const g = (a, b) => cf[a] ?? cf[b] ?? '';
  const kaza = String(g('KazaTarihi', 'kazaTarihi') || '').slice(0, 10);
  const policeYili = kaza ? Number(kaza.slice(0, 4)) : new Date().getFullYear();
  return {
    plaka: g('MuvekkilPlaka', 'muvekkilPlaka') || '',
    marka: '', model: '', modelYili: '', yakit: 'benzin',
    // TSB listesinden seçimde marka/paket kimlikleri; elleArac: listede yok, elle yazılıyor.
    tsbMarkaId: '', tsbModelId: '', elleArac: false,
    // Tam sayfada seçilen dosya { id, foyNo, muvekkil } — rapor bu dosyaya eklenir.
    dosya: null,
    grup: 'otomobil', kullanim: 'hususi',
    kazaTarihi: kaza, km: '',
    sbmKayit: '0', sbmTutar: '', sbmAciklama: '', pert: false,
    parcalar: [],
    iscilik: { kaporta: '', boya: '', mekanik: '', elektrik: '', diger: '' },
    iskonto: '0', kiymet: '', kdvOrani: '20', kdvDahil: true, odenenHasar: '',
    // hasarGirisi: 'ayrinti' (parça fiyatı + işçilik) ya da 'toplam' (parça fiyatları
    // bilinmiyor; yalnız parça + işçilik toplamı bildirilmiş).
    hasarGirisi: 'ayrinti', toplamTutar: '', toplamKdvIcinde: false,
    emsaller: [yeniEmsal(), yeniEmsal(), yeniEmsal()],
    hasarsizDogrudan: '', hasarliDogrudan: '',
    endeksAcik: false, arastirmaTarihi: bugun(), tufeKaza: '', tufeArastirma: '',
    odenenDk: '',
    // tsbOtomatik: TSB kutusunu kullanıcı değil liste doldurdu (araç değişince güncellenir).
    ek1Acik: false, tsb: '', tsbOtomatik: false, tobb: '',
    // Ekspertiz raporundaki piyasa değeri: emsal yokken değer kaybı tahmininin dayanağı.
    eksperPiyasa: '',
    mahrumiyetAcik: false, gunlukKira: '', zorunluGider: '', onarimGun: '',
    kusur: '100',
    policeYili: String(policeYili),
    limit: limitYaz(policeYili),
    basvuruTarihi: String(g('SigortaBasvuruTarihi', 'sigortaBasvuruTarihi') || '').slice(0, 10),
    karsiSigorta: g('KarsiTarafSigortaSirketi', 'karsiTarafSigortaSirketi') || '',
  };
};

const TaslakAnahtari = (id) => `aracHasarTaslak:${id || 'genel'}`;

/* Tutar kutusu. Bilesenin DISINDA tanimli: icinde tanimlansaydi her
   tusta yeni bir bilesen sayilip kutu bastan kurulur, imlec kaybolurdu.
   Kutudan cikinca binlik ayraciyla yazilir (300000 -> 300.000). */
const Tutar = ({ alan, deger, onChange, ph = '0,00', id }) => (
  <input id={id} className="ah-inp ah-sag" inputMode="decimal" placeholder={ph} value={deger}
    onChange={(e) => onChange(e.target.value)} aria-label={alan}
    onBlur={(e) => { const y = tutarYaz(e.target.value); if (y !== e.target.value) onChange(y); }} />
);

/** Formda kullanıcının girdiği bir şey var mı? (Dosyadan gelen plaka / tarih sayılmaz.) */
const formDoluMu = (f) => !!f && !!(
  (f.parcalar || []).length
  || Object.values(f.iscilik || {}).some((v) => String(v || '').trim())
  || String(f.toplamTutar || '').trim()
  || (f.emsaller || []).some((e) => e.kaynak || e.hasarsiz || e.hasarli)
  || f.hasarsizDogrudan || f.hasarliDogrudan || f.marka || f.model || f.tsbMarkaId
  || f.kiymet || f.odenenHasar || f.odenenDk || f.sbmTutar || f.gunlukKira || f.onarimGun || f.eksperPiyasa
);

const AracHasarHesap = ({ caseFile = null, embedded = false, onKapat = null }) => {
  const cf = caseFile || {};
  const dosyaId = cf.Id ?? cf.id ?? null;
  const anahtar = TaslakAnahtari(dosyaId);

  // Kayıtlı taslak (_zaman: son değişiklik). Sayfa yeniden açılınca önceki hesap
  // geri gelir; üstteki şerit bunu söyler ve "Formu temizle"yi gösterir.
  const [ilkTaslak] = useState(() => {
    try {
      const k = localStorage.getItem(anahtar);
      return k ? JSON.parse(k) : null;
    } catch (e) { return null; }
  });
  const [f, setF] = useState(() => {
    if (!ilkTaslak) return bosForm(cf);
    const { _zaman, ...taslak } = ilkTaslak;
    return { ...bosForm(cf), ...taslak };
  });
  const [acilanTaslak, setAcilanTaslak] = useState(() => (formDoluMu(ilkTaslak) ? (ilkTaslak._zaman || true) : null));
  const [bolge, setBolge] = useState('Ön');
  const [dayanakAcik, setDayanakAcik] = useState(false);
  const [durum, setDurum] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);

  // Tam sayfada rapor eklenecek dosyanın seçimi
  const [dosyalar, setDosyalar] = useState([]);
  const [dosyaAra, setDosyaAra] = useState('');
  const [secilenDosyaId, setSecilenDosyaId] = useState(() => (f.dosya?.id ? String(f.dosya.id) : ''));

  // Taslak: her değişiklikte sakla (yazarken kaybolmasın).
  const ilk = useRef(true);
  useEffect(() => {
    if (ilk.current) { ilk.current = false; return; }
    try { localStorage.setItem(anahtar, JSON.stringify({ ...f, _zaman: Date.now() })); } catch (e) { /* yoksay */ }
  }, [f, anahtar]);

  const set = (alan, deger) => setF((o) => ({ ...o, [alan]: deger }));
  const setIscilik = (alan, deger) => setF((o) => ({ ...o, iscilik: { ...o.iscilik, [alan]: deger } }));

  /* ---------------- Araç kimliği (TSB) ve dosyadan doldurma ---------------- */
  const tsb = useTsb(f, setF);

  // Tam sayfada dosya seçilince: dosyadaki plaka, kaza ve sigorta bilgileri.
  // Kullanıcı bu dosyayı açıkça seçtiği için bu alanlar dosyadakiyle değişir.
  const dosyaSec = (d) => {
    const al = (ad) => d?.[ad] ?? d?.[ad.charAt(0).toLowerCase() + ad.slice(1)];
    const kaza = String(al('KazaTarihi') || '').slice(0, 10);
    const basvuru = String(al('SigortaBasvuruTarihi') || '').slice(0, 10);
    setSecilenDosyaId(String(al('Id')));
    setF((o) => ({
      ...o,
      dosya: { id: al('Id'), foyNo: al('FoyNo'), muvekkil: al('Muvekkil') },
      plaka: al('MuvekkilPlaka') || o.plaka,
      kazaTarihi: kaza || o.kazaTarihi,
      basvuruTarihi: basvuru || o.basvuruTarihi,
      karsiSigorta: al('KarsiTarafSigortaSirketi') || o.karsiSigorta,
      ...(kaza ? { policeYili: kaza.slice(0, 4), limit: limitYaz(Number(kaza.slice(0, 4))) || o.limit } : {}),
    }));
  };
  const dosyaBirak = () => { setSecilenDosyaId(''); set('dosya', null); };

  /**
   * Ekspertiz raporundan okunanları forma işler: parçalar (değişen, onarılan,
   * boyanan), işçilik, parça iskontosu, araç (TSB listesinden), km, kaza
   * tarihi, karşı tarafın kusuru, onarım süresi. Formda parça varsa önce sorar.
   * Kısa özet döner; vazgeçilirse null.
   */
  const ekspertizUygula = async (s) => {
    if (f.parcalar.length && !(await onayla(
      `Formdaki ${f.parcalar.length} parça, ekspertiz raporundaki ${s.parcalar.length} parçayla değiştirilsin mi?`,
      { onay: 'Değiştir' },
    ))) return null;

    const degisenler = s.parcalar.filter((p) => p.islem === 'degisim');
    const brut = degisenler.reduce((t, p) => t + (p.bedel || 0) * (p.adet || 1), 0);
    const iskontoTutar = degisenler.reduce((t, p) => t + (p.iskonto || 0), 0);
    // Oran 4 basamakla: parça başına farklı iskonto olan raporlarda (OtoHasar %35 / %15)
    // 2 basamağa yuvarlamak toplamı birkaç lira kaydırıyordu.
    const iskontoOrani = s.iskontoOrani != null ? s.iskontoOrani
      : (brut > 0 && iskontoTutar > 0 ? Math.round((iskontoTutar / brut) * 1000000) / 10000 : 0);

    let aracOzet = '';
    if (s.arac.marka || s.arac.modelYili) {
      const r = await tsb.ruhsattanUygula({
        marka: s.arac.marka, ticariAdi: s.arac.tip, modelYili: Number(s.arac.modelYili) || null,
        plaka: s.arac.plaka, grup: s.arac.grup, kullanim: s.arac.kullanim, yakit: s.arac.yakit,
      });
      aracOzet = r.ozet;
    }

    const zaman = Date.now();
    const yaz = (v) => (v > 0 ? tutarYaz(round2(v)) : '');
    // Raporda fiyat ya da işçilik okunduysa hasar ayrıntılı girişle hesaplanır.
    const fiyatVar = brut > 0 || Object.values(s.iscilik).some((v) => v > 0);
    setF((o) => ({
      ...o,
      ...(fiyatVar ? { hasarGirisi: 'ayrinti' } : {}),
      parcalar: s.parcalar.map((p, i) => ({
        uid: `eks-${zaman}-${i}`,
        kod: p.kod,
        ad: p.ad,
        islem: p.islem,
        // Rapor derecesini yazıyorsa (OtoHasar "ORTA") o; yoksa Ek-1 m.4/2 gereği yüksek.
        onarimSeviyesi: p.onarimSeviyesi || 'yuksek',
        boya: p.boya || 'yok',
        bedel: p.islem === 'degisim' ? yaz(p.bedel) : '',
        adet: String(p.adet || 1),
        oncekiHasar: false,
      })),
      iscilik: {
        kaporta: yaz(s.iscilik.kaporta), boya: yaz(s.iscilik.boya), mekanik: yaz(s.iscilik.mekanik),
        elektrik: yaz(s.iscilik.elektrik), diger: yaz(s.iscilik.diger),
      },
      // Virgülle yazılır: "34.851" gibi üç basamaklı ondalık, noktalı yazımda binlik sanılırdı.
      iskonto: String(iskontoOrani).replace('.', ','),
      km: o.km || (Number(s.arac.km) > 0 ? tutarYaz(s.arac.km) : ''),
      kazaTarihi: o.kazaTarihi || s.arac.hasarTarihi || '',
      kusur: s.arac.karsiKusur ? String(s.arac.karsiKusur) : o.kusur,
      onarimGun: o.onarimGun || s.arac.onarimGun || '',
      eksperPiyasa: s.arac.piyasaBedeli > 0 ? tutarYaz(s.arac.piyasaBedeli) : o.eksperPiyasa,
    }));

    // Yanlış rapor seçilmiş olabilir (ör. karşı tarafın aracı): formdakiyle uyuşmayanı söyle.
    const sadePlaka = (p) => String(p || '').toLocaleUpperCase('tr').replace(/\s+/g, '');
    if (f.plaka && s.arac.plaka && sadePlaka(f.plaka) !== sadePlaka(s.arac.plaka)) {
      s.uyarilar = [...(s.uyarilar || []), `Rapordaki plaka (${s.arac.plaka}) formdaki plakadan (${f.plaka}) farklı. Doğru rapor mu, kontrol edin.`];
    }
    if (f.kazaTarihi && s.arac.hasarTarihi && f.kazaTarihi !== s.arac.hasarTarihi) {
      s.uyarilar = [...(s.uyarilar || []), `Rapordaki hasar tarihi (${tarihTR(s.arac.hasarTarihi)}) formdaki kaza tarihinden (${tarihTR(f.kazaTarihi)}) farklı; formdaki tarih korundu.`];
    }

    const iscilikToplam = Object.values(s.iscilik).reduce((t, v) => t + v, 0);
    const parcaParca = [
      degisenler.length ? `${degisenler.length} değişen parça (${tl(degisenler.reduce((t, p) => t + (p.tutar || 0), 0))} TL)` : '',
      s.parcalar.length - degisenler.length ? `${s.parcalar.length - degisenler.length} onarılan/boyanan parça` : '',
      iscilikToplam ? `işçilik ${tl(iscilikToplam)} TL` : '',
      s.arac.piyasaBedeli ? `eksperin piyasa bedeli ${tl(s.arac.piyasaBedeli)} TL` : '',
    ].filter(Boolean);
    return [aracOzet, ...parcaParca].filter(Boolean).join(' · ');
  };

  /* ---------------- Parçalar ---------------- */
  const parcaEkle = (kod) => {
    const p = parcaBul(kod);
    if (!p) return;
    setF((o) => ({
      ...o,
      parcalar: [...o.parcalar, {
        uid: `${kod}-${Date.now()}`,
        kod,
        islem: p.ek1 ? 'onarim' : 'degisim',
        // Ek-1 m.4/2: derece belirlenemiyorsa "yuksek" uygulanir.
        onarimSeviyesi: 'yuksek',
        boya: p.ek1 && p.Y ? 'tam' : 'yok',
        bedel: '',
        adet: '1',
        oncekiHasar: false,
      }],
    }));
  };
  const parcaGuncelle = (uid, alan, deger) =>
    setF((o) => ({ ...o, parcalar: o.parcalar.map((p) => (p.uid === uid ? { ...p, [alan]: deger } : p)) }));
  const parcaSil = (uid) => setF((o) => ({ ...o, parcalar: o.parcalar.filter((p) => p.uid !== uid) }));

  // Listede olan parçaya yeniden dokununca ikinci satır açılmaz; o satıra gidilir.
  const [vurgu, setVurgu] = useState(null);
  const vurguZaman = useRef(null);
  useEffect(() => () => clearTimeout(vurguZaman.current), []);
  const parcaDokun = (kod) => {
    const mevcut = f.parcalar.find((r) => r.kod === kod);
    if (!mevcut) { parcaEkle(kod); return; }
    const el = document.getElementById(`ah-p-${mevcut.uid}`);
    const yavas = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (el) el.scrollIntoView({ behavior: yavas ? 'smooth' : 'auto', block: 'center' });
    setVurgu(mevcut.uid);
    clearTimeout(vurguZaman.current);
    vurguZaman.current = setTimeout(() => setVurgu(null), 1400);
  };

  const emsalGuncelle = (i, alan, deger) =>
    setF((o) => ({ ...o, emsaller: o.emsaller.map((e, j) => (j === i ? { ...e, [alan]: deger } : e)) }));

  /* ---------------- Hesaplar ---------------- */
  const kod = grupKodu(f.grup);
  const ticari = f.kullanim !== 'hususi';

  const toplamModu = f.hasarGirisi === 'toplam';
  const hasar = useMemo(() => maddiHasarHesapla({
    kalemler: f.parcalar.filter((p) => p.islem === 'degisim').map((p) => ({ bedel: sayi(p.bedel), adet: sayi(p.adet) || 1 })),
    iskontoOrani: sayi(f.iskonto),
    kiymetKazanma: sayi(f.kiymet),
    iscilikler: Object.fromEntries(Object.entries(f.iscilik).map(([k, v]) => [k, sayi(v)])),
    kdvOrani: sayi(f.kdvOrani),
    kdvDahil: f.kdvDahil,
    kusurOrani: sayi(f.kusur),
    odenen: sayi(f.odenenHasar),
    birlesikToplam: toplamModu ? sayi(f.toplamTutar) : null,
    birlesikKdvIcinde: f.toplamKdvIcinde,
  }), [f.parcalar, f.iskonto, f.kiymet, f.iscilik, f.kdvOrani, f.kdvDahil, f.kusur, f.odenenHasar, toplamModu, f.toplamTutar, f.toplamKdvIcinde]);

  // Değişen parçaların hiçbirine fiyat yazılmadıysa "yalnız toplam" girişi önerilir.
  const degisenSayisi = f.parcalar.filter((p) => p.islem === 'degisim').length;
  const fiyatGirilmedi = degisenSayisi > 0 && f.parcalar.every((p) => p.islem !== 'degisim' || !sayi(p.bedel));
  // 3. bölümdeki bağlantıyla geçilince 4. bölümdeki toplam kutusuna gidilir.
  const toplamaGidilecek = useRef(false);
  const toplamaGec = () => { toplamaGidilecek.current = true; set('hasarGirisi', 'toplam'); };
  useEffect(() => {
    if (!toplamModu || !toplamaGidilecek.current) return;
    toplamaGidilecek.current = false;
    const el = document.getElementById('ah-toplam-tutar');
    if (!el) return;
    const yavas = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: yavas ? 'smooth' : 'auto', block: 'center' });
    el.focus({ preventScroll: true });
  }, [toplamModu]);

  const endeks = f.endeksAcik ? endeksKatsayisi(sayi(f.tufeKaza), sayi(f.tufeArastirma)) : 1;

  const dk = useMemo(() => degerKaybiPiyasa({
    emsaller: f.emsaller,
    hasarsizDogrudan: f.hasarsizDogrudan,
    hasarliDogrudan: f.hasarliDogrudan,
    endeks,
    kusurOrani: sayi(f.kusur),
    odenen: sayi(f.odenenDk),
    hasarTutari: hasar.toplam,
  }), [f.emsaller, f.hasarsizDogrudan, f.hasarliDogrudan, endeks, f.kusur, f.odenenDk, hasar.toplam]);

  // 6. bölüm: eski Ek-1 kuralıyla (TSB / TOBB listesi önce) — sigortacının hesabıyla karşılaştırma.
  const ek1 = useMemo(() => degerKaybiEk1({
    grupId: f.grup,
    tsbDegeri: f.tsb,
    tobbDegeri: f.tobb,
    arastirmaDegeri: dk.hasarsiz || sayi(f.eksperPiyasa),
    arastirmaKaynak: dk.hasarsiz > 0 ? 'Değer araştırması (emsaller)' : 'Ekspertiz raporundaki piyasa değeri',
    kmVeyaSaat: sayi(f.km),
    hasarliParcalar: f.parcalar,
    hasarTutari: hasar.brutKdvDahil,
    ticari,
    sbmKayitSayisi: sayi(f.sbmKayit),
  }), [f.grup, f.tsb, f.tobb, dk.hasarsiz, f.eksperPiyasa, f.km, f.parcalar, hasar.brutKdvDahil, ticari, f.sbmKayit]);

  /* EMSAL YOKKEN DEĞER KAYBI (kullanıcı isteği, 2026-10-06): hasarsız ve onarılmış
     fiyat girilmediyse değer kaybı ekspertizdeki verilerle formülden tahmin edilir.
     Piyasa değeri ikinci el değerinden: girilen hasarsız rayiç, yoksa eksperin
     piyasa değeri (Yargıtay: kasko listesi değil serbest piyasa değeri); ikisi de
     yoksa 6. bölümdeki liste değeri. Emsal girilince emsal esas alınır. */
  const ek1Tahmin = useMemo(() => {
    const piyasa = dk.hasarsiz > 0 ? [dk.hasarsiz, 'girdiğiniz hasarsız rayiç']
      : sayi(f.eksperPiyasa) > 0 ? [sayi(f.eksperPiyasa), 'ekspertiz raporundaki piyasa değeri'] : null;
    if (!piyasa) return ek1;
    return degerKaybiEk1({
      grupId: f.grup,
      arastirmaDegeri: piyasa[0],
      arastirmaKaynak: piyasa[1],
      kmVeyaSaat: sayi(f.km),
      hasarliParcalar: f.parcalar,
      hasarTutari: hasar.brutKdvDahil,
      ticari,
      sbmKayitSayisi: sayi(f.sbmKayit),
    });
  }, [ek1, dk.hasarsiz, f.eksperPiyasa, f.grup, f.km, f.parcalar, hasar.brutKdvDahil, ticari, f.sbmKayit]);

  const emsalTam = dk.hasarsiz > 0 && dk.hasarli > 0;
  const dkSonuc = !emsalTam && ek1Tahmin.DK > 0
    ? degerKaybiFormulle(ek1Tahmin, { kusurOrani: sayi(f.kusur), odenen: sayi(f.odenenDk), hasarTutari: hasar.toplam })
    : dk;
  const formulTahmini = dkSonuc.yontem === 'formul';

  const mahrumiyet = useMemo(() => mahrumiyetHesapla({
    gunlukKira: sayi(f.gunlukKira),
    zorunluGider: sayi(f.zorunluGider),
    gun: sayi(f.onarimGun),
    kusurOrani: sayi(f.kusur),
  }), [f.gunlukKira, f.zorunluGider, f.onarimGun, f.kusur]);

  const dkBakiye = f.pert ? 0 : dkSonuc.bakiye;
  const ozet = sonucOzeti({
    hasarBakiye: hasar.bakiye,
    dkBakiye,
    mahrumiyet: f.mahrumiyetAcik ? mahrumiyet.tutar : 0,
    limit: sayi(f.limit),
  });

  const isGunu = f.basvuruTarihi ? isGunuEkle(f.basvuruTarihi, 8) : null;
  const temerrut = isGunu?.tarih || null;
  const zamanasimi10 = f.kazaTarihi ? yilEkle(f.kazaTarihi, 10) : null;
  const zamanasimi2 = f.kazaTarihi ? yilEkle(f.kazaTarihi, 2) : null;

  /* ---------------- Uyarılar ---------------- */
  const uyarilar = [];
  if (f.pert) uyarilar.push({ t: 'hata', m: 'Araç pert (tam hasar) ise ayrıca değer kaybı istenmez; zarar, kaza tarihindeki rayiç değer ile hurda (sovtaj) bedeli arasındaki farktır (Yargıtay 17. HD 2013/5126 E. 2013/8212 K.).' });
  if (f.kazaTarihi && f.kazaTarihi < '2026-07-01') uyarilar.push({ t: 'bilgi', m: 'Kaza 01.07.2026\'dan önce: sigortacı eski formülle (Ek-1) teklif yapmış olabilir. Tahkim ve mahkeme ikinci el değer farkını (piyasa yöntemini) esas alır.' });
  if (formulTahmini && !f.pert) uyarilar.push({ t: 'bilgi', m: `Değer kaybı emsal girilmediği için ekspertizdeki verilerle formülden tahmin edildi (piyasa değeri: ${dkSonuc.piyasaKaynak}). Mahkeme ve tahkim ikinci el piyasa farkını esas alır; itirazda emsal araştırması gerekebilir. Emsal girerseniz emsal esas alınır.` });
  const secili = f.parcalar.map((p) => ({ ...p, bilgi: parcaBul(p.kod) }));
  if (secili.length && secili.every((p) => !p.bilgi?.ek1)) uyarilar.push({ t: 'uyari', m: 'Seçilen parçaların hepsi boyasız/vidalı veya mekanik parça. Bu parçalar tek başına genellikle değer kaybı doğurmaz; kaporta (sac) parçası işlem gördüyse ekleyin.' });
  const oncekiler = secili.filter((p) => p.oncekiHasar);
  if (secili.length && !toplamModu && !(hasar.iscilikToplam > 0)) uyarilar.push({ t: 'uyari', m: 'İşçilik girilmedi: toplam hasar yalnız parça bedelinden oluşuyor. İşçiliği ekspertiz raporundan ya da servis faturasından girin.' });
  if (toplamModu && !(hasar.birlesik > 0)) uyarilar.push({ t: 'uyari', m: 'Parça + işçilik toplamı girilmedi: 4. bölüme bildirilen toplam tutarı yazın.' });
  if (oncekiler.length) uyarilar.push({ t: 'uyari', m: `Önceden hasarlı parça: ${oncekiler.map((p) => p.ad || p.bilgi?.ad).join(', ')}. Bu parçalar için mükerrer değer kaybı istenemez; onarılmış emsal araştırmasında bu durum dikkate alınmalı (Yargıtay 4. HD 2024/3822 E. 2024/10404 K.).` });
  if (!formulTahmini && dk.hasarsizAdet > 0 && dk.hasarsizAdet < 3) uyarilar.push({ t: 'bilgi', m: 'Hasarsız emsal sayısı 3\'ten az. Yargıtay somut piyasa araştırması ve birden fazla emsal arıyor; en az 3 emsal önerilir.' });
  if (ozet.limitiAsan > 0) uyarilar.push({ t: 'uyari', m: `Hasar + değer kaybı poliçe limitini ${tl(ozet.limitiAsan)} TL aşıyor; aşan kısım sürücü/işleten/malikten istenir.` });

  /* ---------------- Rapor ---------------- */
  const bilgi = () => ({
    foyNo: cf.FoyNo ?? cf.foyNo ?? f.dosya?.foyNo ?? '',
    muvekkil: cf.Muvekkil ?? cf.muvekkil ?? f.dosya?.muvekkil ?? '',
    karsiSigorta: f.karsiSigorta,
    form: f,
    grupAdi: (ARAC_GRUPLARI.find((x) => x.id === f.grup) || {}).ad,
  });
  const rapor = () => aracHasarWordUret({ hasar, dk: dkSonuc, ek1, ek1Tahmin, mahrumiyet, ozet, temerrut, zamanasimi10, zamanasimi2, uyarilar }, bilgi());

  const indir = () => {
    try {
      const { blob, dosyaAdi } = rapor();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = dosyaAdi; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setDurum({ tip: 'ok', metin: 'Word belgesi indirildi.' });
    } catch (e) {
      setDurum({ tip: 'hata', metin: 'Belge oluşturulamadı: ' + (e?.message || '') });
    }
  };

  const dosyalariGetir = async () => {
    if (dosyalar.length) return;
    try {
      const l = await caseFileAPI.getAll();
      setDosyalar(Array.isArray(l) ? l : []);
    } catch (e) { setDurum({ tip: 'hata', metin: 'Dosya listesi alınamadı.' }); }
  };
  const filtreliDosyalar = useMemo(() => {
    const q = dosyaAra.trim().toLocaleLowerCase('tr');
    const s = (a, b) => Number(b.FoyNo ?? b.foyNo ?? 0) - Number(a.FoyNo ?? a.foyNo ?? 0);
    const l = q ? dosyalar.filter((d) => String(d.FoyNo ?? d.foyNo ?? '').includes(q)
      || String(d.Muvekkil ?? d.muvekkil ?? '').toLocaleLowerCase('tr').includes(q)) : dosyalar;
    return [...l].sort(s).slice(0, 80);
  }, [dosyalar, dosyaAra]);

  const dosyayaEkle = async () => {
    const id = dosyaId || f.dosya?.id || secilenDosyaId;
    if (!id) { setDurum({ tip: 'hata', metin: 'Önce bir dosya seçin.' }); return; }
    setYukleniyor(true);
    setDurum({ tip: 'bilgi', metin: 'Rapor hazırlanıyor…' });
    try {
      const { blob, dosyaAdi } = rapor();
      const file = new File([blob], dosyaAdi, { type: 'application/msword' });
      await documentAPI.upload(id, file, 'Araç hasar ve değer kaybı hesap raporu', 0);
      setDurum({ tip: 'ok', metin: 'Rapor dosyanın belgelerine eklendi.' });
    } catch (e) {
      setDurum({ tip: 'hata', metin: 'Yüklenemedi: ' + (e?.response?.data?.message || e?.message || '') });
    } finally { setYukleniyor(false); }
  };

  const temizle = async () => {
    if (!(await onayla('Girilen bütün bilgiler silinsin mi?', { onay: 'Temizle', tehlikeli: true }))) return;
    try { localStorage.removeItem(anahtar); } catch (e) { /* yoksay */ }
    setF(bosForm(cf));
    setSecilenDosyaId('');
    setDurum(null);
    setAcilanTaslak(null);
  };
  const formDolu = formDoluMu(f);

  const bolgeParcalari = PARCALAR.filter((p) => p.bolge === bolge);
  const ilanlar = ilanBaglantilari({ marka: f.marka, model: f.model, yil: f.modelYili, km: f.km });

  return (
    <div
      className={`ah${embedded ? ' ah-gomulu' : ''}`}
      // Dosya formunun icinde acildiginda Enter tusu dosyayi kaydetmesin.
      onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}
    >
      {embedded && (
        <div className="ah-gomulu-ust">
          <strong>Araç hasar ve değer kaybı hesabı</strong>
          {onKapat && <button type="button" className="ah-kapat" onClick={onKapat} aria-label="Kapat">✕</button>}
        </div>
      )}

      {/* Formda bilgi varken üstte: önceki hesap mı açıldı, yoksa yeni mi giriliyor;
          "Formu temizle" sayfanın en altına inmeden buradan da yapılır. */}
      {formDolu && (
        <div className={`ah-taslak${acilanTaslak ? ' acildi' : ''}`} role="status">
          <span className="ah-taslak-metin">
            {acilanTaslak ? (
              <>
                <b>Önceki hesabınız açıldı</b>
                {typeof acilanTaslak === 'number' && (
                  <> · son değişiklik {new Date(acilanTaslak).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                )}
                . Yeni hesap için formu temizleyin.
              </>
            ) : 'Girdikleriniz bu cihazda saklanıyor; sayfadan çıkıp girince kaybolmaz.'}
          </span>
          <button type="button" className="ah-taslak-temizle" onClick={temizle}>Formu temizle</button>
        </div>
      )}

      <div className="ah-izgara">
        {/* ======================= SOL: GİRİŞLER ======================= */}
        <div className="ah-sol">
          {/* 0. Dosyadan ve ruhsattan doldur */}
          <DosyaRuhsat
            embedded={embedded}
            dosyaId={dosyaId}
            seciliDosya={f.dosya}
            paketSecili={!!f.tsbModelId}
            onDosyaSec={dosyaSec}
            onDosyaBirak={dosyaBirak}
            onRuhsat={tsb.ruhsattanUygula}
            onEkspertiz={ekspertizUygula}
          />

          {/* 1. Araç ve kaza */}
          <section className="ah-kart">
            <h3 className="ah-baslik"><span className="ah-no">1</span>Araç ve kaza</h3>
            {/* Sıra: yıl → marka → paket (TSB listesi bu sırayla daralır). Izgara
                "dense": paket iki sütun kapladığında boşluğa plaka yerleşir. */}
            <div className="ah-3 ah-arac">
              <AracSecimAlanlari f={f} set={set} tsb={tsb} />
              <label className="ah-alan"><span>Plaka</span>
                <input className="ah-inp" value={f.plaka} onChange={(e) => set('plaka', e.target.value.toLocaleUpperCase('tr'))} placeholder="ör. 06 ABC 123" /></label>
              <label className="ah-alan"><span>Kaza tarihi</span>
                <input className="ah-inp" type="date" value={f.kazaTarihi} onChange={(e) => set('kazaTarihi', e.target.value)} /></label>
              <label className="ah-alan"><span>{kod === 'D' ? 'Çalışma saati' : 'Kaza tarihindeki km'}</span>
                <input className="ah-inp ah-sag" inputMode="numeric" value={f.km} onChange={(e) => set('km', e.target.value)} placeholder="ör. 246.648"
                  onBlur={(e) => set('km', tutarYaz(e.target.value))} /></label>
              <label className="ah-alan"><span>Araç grubu</span>
                <select className="ah-inp" value={f.grup} onChange={(e) => set('grup', e.target.value)}>
                  {ARAC_GRUPLARI.map((g) => <option key={g.id} value={g.id}>{g.ad}</option>)}
                </select></label>
              <label className="ah-alan"><span>Kullanım şekli</span>
                <select className="ah-inp" value={f.kullanim} onChange={(e) => set('kullanim', e.target.value)}>
                  <option value="hususi">Hususi</option>
                  <option value="ticari">Ticari</option>
                  <option value="kiralik">Kiralık (rent a car)</option>
                  <option value="taksi">Taksi / dolmuş</option>
                </select></label>
              <label className="ah-alan"><span>Yakıt</span>
                <select className="ah-inp" value={f.yakit} onChange={(e) => set('yakit', e.target.value)}>
                  <option value="benzin">Benzin</option>
                  <option value="dizel">Dizel</option>
                  <option value="lpg">Benzin + LPG</option>
                  <option value="hibrit">Hibrit</option>
                  <option value="elektrik">Elektrikli</option>
                </select></label>
            </div>
            <TsbBilgi f={f} tsb={tsb} />
          </section>

          {/* 2. Önceki hasar */}
          <section className="ah-kart">
            <h3 className="ah-baslik"><span className="ah-no">2</span>Önceki hasar kaydı (SBM / Tramer)</h3>
            <div className="ah-3">
              <label className="ah-alan"><span>Kayıt sayısı</span>
                <input className="ah-inp ah-sag" inputMode="numeric" value={f.sbmKayit} onChange={(e) => set('sbmKayit', e.target.value.replace(/\D/g, ''))} /></label>
              <label className="ah-alan"><span>Toplam tutar (TL)</span>
                <Tutar alan="SBM toplam" deger={f.sbmTutar} onChange={(v) => set('sbmTutar', v)} /></label>
              <label className="ah-alan ah-tel-tam"><span>Ne olduğu</span>
                <input className="ah-inp" value={f.sbmAciklama} onChange={(e) => set('sbmAciklama', e.target.value)} placeholder="ör. 26.10.2022, içeriği bilinmiyor" /></label>
            </div>
            <label className="ah-onay">
              <input type="checkbox" checked={f.pert} onChange={(e) => set('pert', e.target.checked)} />
              Araç bu kazada pert (tam hasar) oldu / ağır hasar kaydı var
            </label>
            <p className="ah-not">Aynı parçada önceki hasar varsa aşağıdaki parça listesinde “Önceden hasarlı” kutusunu işaretleyin.</p>
          </section>

          {/* 3. Parçalar */}
          <section className="ah-kart">
            <h3 className="ah-baslik"><span className="ah-no">3</span>Hasar gören parçalar</h3>
            <div className="ah-bolgeler" role="tablist" aria-label="Araç bölgesi">
              {BOLGELER.map((b) => {
                const adet = f.parcalar.filter((r) => parcaBul(r.kod)?.bolge === b).length;
                return (
                  <button key={b} type="button" role="tab" aria-selected={bolge === b}
                    className={`ah-bolge${bolge === b ? ' secili' : ''}`} onClick={() => setBolge(b)}>
                    {b}{adet > 0 && <span className="ah-bolge-sayi" aria-label={`${adet} parça seçili`}>{adet}</span>}
                  </button>
                );
              })}
            </div>
            <div className="ah-parca-sec">
              {bolgeParcalari.map((p) => {
                const e = etkiDuzeyi(p);
                const secildi = f.parcalar.some((r) => r.kod === p.kod);
                return (
                  <button key={p.kod} type="button" className={`ah-parca-dugme etki-${e.id}${secildi ? ' secili' : ''}`}
                    onClick={() => parcaDokun(p.kod)} title={secildi ? 'Listede — satırına git' : e.ad} aria-pressed={secildi}>
                    <span className="ah-parca-ad">{p.ad}</span>
                    <span className="ah-arti" aria-hidden="true">{secildi ? '✓' : '+'}</span>
                  </button>
                );
              })}
            </div>

            {f.parcalar.length === 0 ? (
              <p className="ah-bos">Yukarıdan bölge seçip hasar gören parçalara dokunun.</p>
            ) : (
              <div className="ah-parcalar">
                {f.parcalar.map((r) => {
                  const p = parcaBul(r.kod);
                  const e = etkiDuzeyi(p);
                  return (
                    <div key={r.uid} id={`ah-p-${r.uid}`} className={`ah-parca-satir${vurgu === r.uid ? ' vurgu' : ''}`}>
                      <div className="ah-parca-bas">
                        <div>
                          {/* Ekspertizden gelen satırda raporun parça adı; altında hesaptaki karşılığı */}
                          <div className="ah-parca-isim">{r.ad || p?.ad}</div>
                          {r.ad && r.kod !== 'X.25' && <div className="ah-parca-karsilik">{p?.ad}</div>}
                          <span className={`ah-etki etki-${e.id}`}>{e.ad}</span>
                        </div>
                        <button type="button" className="ah-sil" onClick={() => parcaSil(r.uid)} aria-label={`${r.ad || p?.ad} kaldır`}>✕</button>
                      </div>
                      <div className="ah-parca-alanlar">
                        <label className="ah-alan"><span>İşlem</span>
                          <select className="ah-inp" value={r.islem} onChange={(ev) => parcaGuncelle(r.uid, 'islem', ev.target.value)}>
                            {ISLEMLER.map((i) => <option key={i.id} value={i.id}>{i.ad}</option>)}
                          </select></label>
                        {r.islem === 'onarim' && (
                          <label className="ah-alan"><span>Onarım derecesi</span>
                            <select className="ah-inp" value={r.onarimSeviyesi} onChange={(ev) => parcaGuncelle(r.uid, 'onarimSeviyesi', ev.target.value)}>
                              {ONARIM_SEVIYELERI.map((s) => <option key={s.id} value={s.id}>{s.ad}</option>)}
                            </select></label>
                        )}
                        {r.islem !== 'sokTak' && (
                          <label className="ah-alan"><span>Boya</span>
                            <select className="ah-inp" value={r.boya} onChange={(ev) => parcaGuncelle(r.uid, 'boya', ev.target.value)}>
                              {BOYALAR.map((b) => <option key={b.id} value={b.id}>{b.ad}</option>)}
                            </select></label>
                        )}
                        {r.islem === 'degisim' && !toplamModu && (
                          <>
                            <label className="ah-alan"><span>Parça bedeli (KDV hariç)</span>
                              <Tutar alan="Parça bedeli" deger={r.bedel} onChange={(v) => parcaGuncelle(r.uid, 'bedel', v)} /></label>
                            <label className="ah-alan ah-dar"><span>Adet</span>
                              <input className="ah-inp ah-sag" inputMode="numeric" value={r.adet} onChange={(ev) => parcaGuncelle(r.uid, 'adet', ev.target.value.replace(/\D/g, ''))} /></label>
                          </>
                        )}
                      </div>
                      <label className="ah-onay ah-onay-kucuk">
                        <input type="checkbox" checked={r.oncekiHasar} onChange={(ev) => parcaGuncelle(r.uid, 'oncekiHasar', ev.target.checked)} />
                        Önceden hasarlı (aynı parça daha önce işlem görmüş)
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
            {degisenSayisi > 0 && toplamModu && (
              <p className="ah-not ah-ipucu">Parça fiyatı istenmiyor: hasar, 4. bölüme yazılan parça + işçilik toplamından hesaplanıyor. Parçaları seçmek değer kaybı ve rapor için yeterli.</p>
            )}
            {fiyatGirilmedi && !toplamModu && (
              <p className="ah-not ah-ipucu">
                Parça fiyatlarını bilmiyor musunuz?{' '}
                <button type="button" className="ah-bag" onClick={toplamaGec}>Yalnız parça + işçilik toplamını gireceğim</button>
              </p>
            )}
          </section>

          {/* 4. İşçilik ve maddi hasar */}
          <section className="ah-kart">
            <h3 className="ah-baslik"><span className="ah-no">4</span>İşçilik ve maddi hasar</h3>
            <div className="ah-secim" role="radiogroup" aria-label="Hasar tutarı nasıl girilecek">
              <span className={`ah-secim-kayan${toplamModu ? ' sag' : ''}`} aria-hidden="true" />
              {[['ayrinti', 'Parça ve işçilik ayrı ayrı'], ['toplam', 'Yalnız toplam biliniyor']].map(([id, ad]) => (
                <button key={id} type="button" role="radio" aria-checked={f.hasarGirisi === id}
                  className={`ah-secim-dugme${f.hasarGirisi === id ? ' secili' : ''}`} onClick={() => set('hasarGirisi', id)}>{ad}</button>
              ))}
            </div>
            {toplamModu ? (
              <div key="toplam" className="ah-mod-ic">
                <p className="ah-not ah-mod-not">Parça fiyatları bilinmiyorsa servisin, eksperin ya da sigortanın bildirdiği parça + işçilik toplamını yazın.</p>
                <div className="ah-3">
                  <label className="ah-alan ah-genis"><span>Parça + işçilik toplamı ({f.toplamKdvIcinde ? 'KDV dahil' : 'KDV hariç'})</span>
                    <Tutar id="ah-toplam-tutar" alan="Parça ve işçilik toplamı" deger={f.toplamTutar} onChange={(v) => set('toplamTutar', v)} /></label>
                  {f.toplamKdvIcinde && (
                    <div className="ah-alan"><span>KDV hariç tutarı</span>
                      <div className="ah-gosterge" aria-live="polite">{tl(hasar.birlesik)}</div></div>
                  )}
                </div>
                <label className="ah-onay">
                  <input type="checkbox" checked={f.toplamKdvIcinde} onChange={(e) => set('toplamKdvIcinde', e.target.checked)} />
                  Bu tutara KDV dahil
                </label>
              </div>
            ) : (
              <div key="ayrinti" className="ah-3 ah-mod-ic">
                {[['kaporta', 'Kaporta'], ['boya', 'Boya'], ['mekanik', 'Mekanik'], ['elektrik', 'Elektrik'], ['diger', 'Diğer']].map(([k, ad]) => (
                  <label key={k} className="ah-alan"><span>{ad} işçiliği (KDV hariç)</span>
                    <Tutar alan={`${ad} işçiliği`} deger={f.iscilik[k]} onChange={(v) => setIscilik(k, v)} /></label>
                ))}
                <div className="ah-alan"><span>İşçilik toplamı (KDV hariç)</span>
                  <div className="ah-gosterge" aria-live="polite">{tl(hasar.iscilikToplam)}</div></div>
              </div>
            )}
            <div className="ah-3">
              {!toplamModu && (
                <label className="ah-alan"><span>Parça iskontosu (%)</span>
                  <input className="ah-inp ah-sag" inputMode="decimal" value={f.iskonto} onChange={(e) => set('iskonto', e.target.value)} /></label>
              )}
              <label className="ah-alan"><span>Kıymet kazanma tenzili (TL)</span>
                <Tutar alan="Kıymet kazanma" deger={f.kiymet} onChange={(v) => set('kiymet', v)} /></label>
              <label className="ah-alan"><span>Ödenen hasar (TL)</span>
                <Tutar alan="Ödenen hasar" deger={f.odenenHasar} onChange={(v) => set('odenenHasar', v)} /></label>
              <label className="ah-alan"><span>KDV oranı (%)</span>
                <input className="ah-inp ah-sag" inputMode="decimal" value={f.kdvOrani} onChange={(e) => set('kdvOrani', e.target.value)}
                  disabled={!f.kdvDahil && !(toplamModu && f.toplamKdvIcinde)} /></label>
            </div>
            <label className="ah-onay">
              <input type="checkbox" checked={f.kdvDahil} onChange={(e) => set('kdvDahil', e.target.checked)} />
              KDV dahil hesapla (fatura aranmaz; KDV'yi indirebilen ticari araçta kaldırın)
            </label>
          </section>

          {/* 5. Değer kaybı — piyasa yöntemi */}
          <section className="ah-kart">
            <h3 className="ah-baslik"><span className="ah-no">5</span>Değer kaybı — ikinci el piyasa araştırması</h3>

            {/* Emsal yokken: rapordaki verilerle formülden tahmin. Piyasa değeri burada düzeltilebilir. */}
            <div className={`ah-tahmin-kutu${formulTahmini ? ' etkin' : ''}`}>
              <div className="ah-tahmin-ust">
                <span className="ah-tahmin-baslik">Emsal girmezseniz: rapordaki verilerle hesap</span>
                {formulTahmini && <b className="ah-tahmin-tutar">{tl(dkSonuc.dk)} TL</b>}
              </div>
              <div className="ah-3">
                <label className="ah-alan"><span>Piyasa değeri — hasarsız (TL)</span>
                  <Tutar alan="Eksperin piyasa değeri" deger={f.eksperPiyasa} onChange={(v) => set('eksperPiyasa', v)} ph="ekspertiz raporundan" /></label>
                <p className="ah-not ah-genis">
                  {formulTahmini
                    ? <>Değer kaybı, ekspertizdeki değişen / onarılan / boyalı parçalar, hasar tutarı ve km ile eski Genel Şartlar Ek-1 formülünden hesaplandı (oran {yuzde(dkSonuc.dkOrani)}). Aşağıya emsal girerseniz emsal esas alınır.</>
                    : emsalTam
                      ? 'Emsal girildiği için değer kaybı emsallerden hesaplanıyor.'
                      : 'Ekspertiz raporu okununca eksperin piyasa değeri buraya kendiliğinden gelir; değer kaybı parçalardan hesaplanır.'}
                </p>
              </div>
            </div>

            <p className="ah-not">Her satıra bir galeri veya ilan yazın. “Hasarsız” aynı özellikte hiç hasarsız aracın, “Onarılmış” bu hasarla onarılmış aracın fiyatı. Değer kaybı iki ortalamanın farkıdır.</p>
            {ilanlar ? (
              <div className="ah-ilan">
                <span className="ah-ilan-baslik">İlanlarda ara</span>
                <a className="ah-ilan-btn" href={ilanlar.arabam} target="_blank" rel="noopener noreferrer">arabam.com ↗</a>
                <a className="ah-ilan-btn" href={ilanlar.sahibinden} target="_blank" rel="noopener noreferrer">sahibinden.com ↗</a>
                <p className="ah-not">
                  arabam aynı paket, model yılı{sayi(f.km) > 0 ? ' ve km aralığıyla' : 'yla'} açılır; sahibinden'de yılı ve km'yi sitede seçin.
                  Hasarsız fiyat için “boyasız, değişensiz, tramersiz” ilanlara, onarılmış fiyat için benzer hasar kaydı olanlara bakın.
                </p>
              </div>
            ) : (
              <p className="ah-not ah-ilan-yok">İlan sitelerinde aramak için 1. bölümde marka ve modeli seçin.</p>
            )}
            <div className="ah-emsal-baslik" aria-hidden="true"><span>Kaynak (galeri / ilan)</span><span>Hasarsız (TL)</span><span>Onarılmış (TL)</span><span /></div>
            {f.emsaller.map((e, i) => (
              <div key={i} className="ah-emsal">
                <input className="ah-inp" value={e.kaynak} onChange={(ev) => emsalGuncelle(i, 'kaynak', ev.target.value)} placeholder={`Emsal ${i + 1}`} aria-label={`Emsal ${i + 1} kaynak`} />
                <label className="ah-emsal-hucre"><span className="ah-tel-etiket">Hasarsız (TL)</span>
                  <Tutar alan={`Emsal ${i + 1} hasarsız`} deger={e.hasarsiz} onChange={(v) => emsalGuncelle(i, 'hasarsiz', v)} /></label>
                <label className="ah-emsal-hucre"><span className="ah-tel-etiket">Onarılmış (TL)</span>
                  <Tutar alan={`Emsal ${i + 1} onarılmış`} deger={e.hasarli} onChange={(v) => emsalGuncelle(i, 'hasarli', v)} /></label>
                <button type="button" className="ah-sil" onClick={() => setF((o) => ({ ...o, emsaller: o.emsaller.filter((_, j) => j !== i) }))} aria-label="Satırı sil" disabled={f.emsaller.length <= 1}>✕</button>
              </div>
            ))}
            <button type="button" className="ah-ekle" onClick={() => setF((o) => ({ ...o, emsaller: [...o.emsaller, yeniEmsal()] }))}>+ Emsal ekle</button>

            <details className="ah-detay">
              <summary>Emsal yerine değerleri doğrudan gir</summary>
              <div className="ah-3">
                <label className="ah-alan"><span>Hasarsız rayiç (TL)</span>
                  <Tutar alan="Hasarsız rayiç" deger={f.hasarsizDogrudan} onChange={(v) => set('hasarsizDogrudan', v)} /></label>
                <label className="ah-alan"><span>Onarılmış rayiç (TL)</span>
                  <Tutar alan="Onarılmış rayiç" deger={f.hasarliDogrudan} onChange={(v) => set('hasarliDogrudan', v)} /></label>
              </div>
              <p className="ah-not">Emsal tablosunda fiyat varsa tablo esas alınır.</p>
            </details>

            <label className="ah-onay">
              <input type="checkbox" checked={f.endeksAcik} onChange={(e) => set('endeksAcik', e.target.checked)} />
              Fiyatları kaza tarihine indir (TÜFE ile)
            </label>
            {f.endeksAcik && (
              <div className="ah-3">
                <label className="ah-alan"><span>Araştırma tarihi</span>
                  <input className="ah-inp" type="date" value={f.arastirmaTarihi} onChange={(e) => set('arastirmaTarihi', e.target.value)} /></label>
                <label className="ah-alan"><span>TÜFE — kaza ayı</span>
                  <input className="ah-inp ah-sag" inputMode="decimal" value={f.tufeKaza} onChange={(e) => set('tufeKaza', e.target.value)} placeholder="endeks değeri" /></label>
                <label className="ah-alan"><span>TÜFE — araştırma ayı</span>
                  <input className="ah-inp ah-sag" inputMode="decimal" value={f.tufeArastirma} onChange={(e) => set('tufeArastirma', e.target.value)} placeholder="endeks değeri" /></label>
                <p className="ah-not ah-tam">Katsayı: {endeks.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}. Endeks değerleri TÜİK'in aylık TÜFE (2003=100) tablosundan girilir.</p>
              </div>
            )}
            <div className="ah-3">
              <label className="ah-alan"><span>Ödenen değer kaybı (TL)</span>
                <Tutar alan="Ödenen değer kaybı" deger={f.odenenDk} onChange={(v) => set('odenenDk', v)} /></label>
            </div>
          </section>

          {/* 6. Eski formül */}
          <section className="ah-kart">
            <button type="button" className="ah-acilir" aria-expanded={f.ek1Acik} onClick={() => set('ek1Acik', !f.ek1Acik)}>
              <span className="ah-no">6</span>Eski formülle karşılaştır (Genel Şartlar Ek-1)
              <span className="ah-ok" aria-hidden="true">{f.ek1Acik ? '−' : '+'}</span>
            </button>
            {f.ek1Acik && (
              <div className="ah-acilir-ic">
                <p className="ah-not">01.07.2026'da kaldırılan formül: DK = Piyasa Değeri × R × K × H × G. Sigortacının o tarihten önceki kazalarda nasıl hesapladığını görmek için. Mahkemeyi bağlamaz (AYM 2019/40).</p>
                <p className="ah-not">Onarılan parçanın derecesi (3. bölümde seçilir): onarım işçiliğinin yeni parça bedeline oranı %15'e kadar hafif, %30'a kadar orta, üstü yüksek; belirlenemiyorsa yüksek (Ek-1 m.4/2).</p>
                <div className="ah-3">
                  <label className="ah-alan"><span>TSB kasko değeri (TL)</span>
                    <Tutar alan="TSB kasko değeri" deger={f.tsb} onChange={(v) => setF((o) => ({ ...o, tsb: v, tsbOtomatik: false }))} /></label>
                  <label className="ah-alan"><span>TOBB SEİK değeri (TL)</span>
                    <Tutar alan="TOBB SEİK değeri" deger={f.tobb} onChange={(v) => set('tobb', v)} /></label>
                </div>
                {!ek1.parcaListesiVar && <p className="ah-not ah-uyari-metin">Parça katsayıları bu sürümde yalnız otomobil/taksi için var; bu araç grubunda HK = 0 sayılır.</p>}
                <table className="ah-tablo">
                  <tbody>
                    <tr><td>Piyasa değeri <small>({ek1.piyasaKaynak})</small></td><td>{tl(ek1.piyasa)} TL</td></tr>
                    <tr><td>Rayiç değer katsayısı (R)</td><td>{ek1.R.toLocaleString('tr-TR')}</td></tr>
                    <tr><td>Kullanılmışlık katsayısı (K)</td><td>{ek1.K.toLocaleString('tr-TR')}</td></tr>
                    <tr><td>Hasar katsayısı (HK = ΣP+O+Y)</td><td>{ek1.HK.toLocaleString('tr-TR')}</td></tr>
                    <tr><td>Hasar tutarı katsayısı (T)</td><td>{ek1.T.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}</td></tr>
                    <tr><td>H = (HK + T) ÷ 100</td><td>{ek1.H.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}</td></tr>
                    <tr><td>Genel değerlendirme (G)</td><td>{ek1.G.toLocaleString('tr-TR')}{ek1.G1 ? ' · ticari −0,05' : ''}{ek1.G2 ? ` · SBM ${ek1.G2.toLocaleString('tr-TR')}` : ''}{ek1.G3 ? ' · km sınırı +0,05' : ''}</td></tr>
                    <tr className="ah-tablo-son"><td>Eski formülle değer kaybı</td><td>{tl(ek1.DK)} TL</td></tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* 7. Araç mahrumiyeti */}
          <section className="ah-kart">
            <button type="button" className="ah-acilir" aria-expanded={f.mahrumiyetAcik} onClick={() => set('mahrumiyetAcik', !f.mahrumiyetAcik)}>
              <span className="ah-no">7</span>Araç mahrumiyeti (ikame araç)
              <span className="ah-ok" aria-hidden="true">{f.mahrumiyetAcik ? '−' : '+'}</span>
            </button>
            {f.mahrumiyetAcik && (
              <div className="ah-acilir-ic">
                <p className="ah-not">Trafik sigortası karşılamaz; kusurlu sürücü, işleten ve malikten istenir. Süre servisteki bekleme değil, makul (saf teknik) onarım süresidir.</p>
                <div className="ah-3">
                  <label className="ah-alan"><span>Günlük muadil kira (kaza tarihine göre)</span>
                    <Tutar alan="Günlük kira" deger={f.gunlukKira} onChange={(v) => set('gunlukKira', v)} /></label>
                  <label className="ah-alan"><span>Zorunlu gider tasarrufu (TL/gün)</span>
                    <Tutar alan="Zorunlu gider" deger={f.zorunluGider} onChange={(v) => set('zorunluGider', v)} /></label>
                  <label className="ah-alan"><span>Makul onarım süresi (gün)</span>
                    <input className="ah-inp ah-sag" inputMode="numeric" value={f.onarimGun} onChange={(e) => set('onarimGun', e.target.value.replace(/\D/g, ''))} /></label>
                </div>
                <p className="ah-not">Net günlük {tl(mahrumiyet.net)} TL × {sayi(f.onarimGun) || 0} gün = {tl(mahrumiyet.araToplam)} TL</p>
              </div>
            )}
          </section>

          {/* 8. Kusur, limit, tarihler */}
          <section className="ah-kart">
            <h3 className="ah-baslik"><span className="ah-no">8</span>Kusur, poliçe limiti ve tarihler</h3>
            <div className="ah-3">
              <label className="ah-alan"><span>Karşı tarafın kusuru (%)</span>
                <input className="ah-inp ah-sag" inputMode="decimal" value={f.kusur} onChange={(e) => set('kusur', e.target.value)} /></label>
              <label className="ah-alan"><span>Poliçe başlangıç yılı</span>
                <select className="ah-inp" value={f.policeYili} onChange={(e) => setF((o) => ({ ...o, policeYili: e.target.value, limit: limitYaz(e.target.value) }))}>
                  {[...new Set([2022, 2023, 2024, 2025, 2026, 2027, Number(f.policeYili) || 2026])].sort().map((y) => <option key={y} value={y}>{y}</option>)}
                </select></label>
              <label className="ah-alan"><span>Araç başı maddi limit (TL)</span>
                <Tutar alan="Poliçe limiti" deger={f.limit} onChange={(v) => set('limit', v)} ph="poliçeden girin" /></label>
              <label className="ah-alan"><span>Sigortaya başvuru tarihi</span>
                <input className="ah-inp" type="date" value={f.basvuruTarihi} onChange={(e) => set('basvuruTarihi', e.target.value)} /></label>
              <label className="ah-alan ah-genis"><span>Karşı sigorta şirketi</span>
                <input className="ah-inp" value={f.karsiSigorta} onChange={(e) => set('karsiSigorta', e.target.value)} /></label>
            </div>
          </section>
        </div>

        {/* ======================= SAĞ: SONUÇ ======================= */}
        <aside className="ah-sag-sutun">
          <div className="ah-kart ah-sonuc">
            <h3 className="ah-baslik">Sonuç</h3>

            <div className="ah-sonuc-blok">
              <div className="ah-satir"><span>Toplam hasar (KDV {f.kdvDahil ? 'dahil' : 'hariç'})</span><b>{tl(hasar.toplam)} TL</b></div>
              <div className="ah-satir ah-alt"><span>
                {hasar.toplamGirisi
                  ? `Parça + işçilik ${tl(hasar.birlesik)}${hasar.kiymet ? ` − kıymet ${tl(hasar.kiymet)}` : ''}`
                  : `Parça ${tl(hasar.parcaNet)} + işçilik ${tl(hasar.iscilikToplam)}`}
                {f.kdvDahil ? ` + KDV ${tl(hasar.kdv)}` : ''}
              </span></div>
              {hasar.kusurOrani < 100 && <div className="ah-satir ah-alt"><span>Kusura isabet eden (%{hasar.kusurOrani})</span><span>{tl(hasar.kusurluTutar)} TL</span></div>}
              {hasar.odenen > 0 && <div className="ah-satir ah-alt"><span>Ödenen</span><span>− {tl(hasar.odenen)} TL</span></div>}
              <div className="ah-satir ah-vurgu"><span>Bakiye hasar</span><b>{tl(hasar.bakiye)} TL</b></div>
            </div>

            <div className="ah-sonuc-blok">
              {formulTahmini ? (
                <>
                  <div className="ah-satir"><span>Piyasa değeri <small className="ah-kaynak">({dkSonuc.piyasaKaynak})</small></span><span>{tl(dkSonuc.hasarsiz)} TL</span></div>
                  <div className="ah-satir">
                    <span>Değer kaybı <span className="ah-tahmin">formülle tahmin</span></span>
                    <b>{f.pert ? '— (pert)' : `${tl(dkSonuc.dk)} TL`}</b>
                  </div>
                  <div className="ah-satir ah-alt"><span>Değer kaybı oranı {yuzde(dkSonuc.dkOrani)} · rapordaki parçalardan</span></div>
                </>
              ) : (
                <>
                  <div className="ah-satir"><span>Hasarsız rayiç</span><span>{tl(dk.hasarsiz)} TL</span></div>
                  <div className="ah-satir"><span>Onarılmış rayiç</span><span>{tl(dk.hasarli)} TL</span></div>
                  <div className="ah-satir"><span>Değer kaybı</span><b>{f.pert ? '— (pert)' : `${tl(dk.dk)} TL`}</b></div>
                  {dk.hasarsiz > 0 && (
                    <div className="ah-satir ah-alt"><span>Değer kaybı oranı {yuzde(dk.dkOrani)} · hasar oranı {yuzde(dk.hasarOrani)}</span></div>
                  )}
                </>
              )}
              {dkSonuc.kusurOrani < 100 && <div className="ah-satir ah-alt"><span>Kusura isabet eden (%{dkSonuc.kusurOrani})</span><span>{tl(dkSonuc.kusurluTutar)} TL</span></div>}
              {dkSonuc.odenen > 0 && <div className="ah-satir ah-alt"><span>Ödenen</span><span>− {tl(dkSonuc.odenen)} TL</span></div>}
              <div className="ah-satir ah-vurgu"><span>Bakiye değer kaybı</span><b>{tl(dkBakiye)} TL</b></div>
              {!formulTahmini && dk.eksik && (
                <p className="ah-not">
                  {dk.eksik === 'hasarsiz' ? 'Hasarsız emsal fiyatı girin' : 'Onarılmış emsal fiyatı girin'}
                  {' '}ya da 5. bölümde eksperin piyasa değerini yazın; değer kaybı rapordaki verilerle hesaplanır.
                </p>
              )}
              {f.ek1Acik && !formulTahmini && <div className="ah-satir ah-alt"><span>Eski formülle (karşılaştırma)</span><span>{tl(ek1.DK)} TL</span></div>}
            </div>

            {f.mahrumiyetAcik && (
              <div className="ah-sonuc-blok">
                <div className="ah-satir"><span>Araç mahrumiyeti</span><b>{tl(mahrumiyet.tutar)} TL</b></div>
              </div>
            )}

            <div className="ah-sonuc-blok ah-toplam">
              <div className="ah-satir"><span>Sigortadan istenebilecek</span><b>{tl(ozet.sigortadan)} TL</b></div>
              {(ozet.isletenden > 0) && <div className="ah-satir"><span>Sürücü / işletenden</span><b>{tl(ozet.isletenden)} TL</b></div>}
              <div className="ah-satir ah-buyuk"><span>Toplam talep</span><b>{tl(round2(ozet.sigortadan + ozet.isletenden))} TL</b></div>
            </div>

            {(temerrut || zamanasimi10) && (
              <div className="ah-sonuc-blok ah-tarihler">
                {temerrut && <div className="ah-satir"><span>Sigortacının 8 iş günü süresi doluyor</span><span>{tarihTR(temerrut)}</span></div>}
                {temerrut && (
                  <p className="ah-not">
                    Faiz bu tarihten sonra işler (KTK m.99). Hafta sonları ve resmî tatiller sayılmadı
                    {isGunu.atlanan.length ? ` (aradaki tatil: ${isGunu.atlanan.map(tarihTR).join(', ')})` : ''}.
                    {isGunu.bayramBilinmiyor ? ' Bu yılın bayram tarihleri tabloda yok; denk geliyorsa elle kontrol edin.' : ''}
                  </p>
                )}
                {f.kazaTarihi && <div className="ah-satir"><span>Sürücü / işleten için faiz</span><span>{tarihTR(f.kazaTarihi)}</span></div>}
                {zamanasimi2 && <div className="ah-satir"><span>2 yıllık süre (öğrenme kaza günü ise)</span><span>{tarihTR(zamanasimi2)}</span></div>}
                {zamanasimi10 && <div className="ah-satir"><span>En geç (10 yıl)</span><span>{tarihTR(zamanasimi10)}</span></div>}
              </div>
            )}

            {uyarilar.length > 0 && (
              <ul className="ah-uyarilar">
                {uyarilar.map((u, i) => <li key={i} className={`ah-u-${u.t}`}>{u.m}</li>)}
              </ul>
            )}

            <div className="ah-rapor">
              <button type="button" className="ah-btn ah-btn-ana" onClick={indir}>Word rapor indir</button>
              {dosyaId || f.dosya ? (
                <button type="button" className="ah-btn" onClick={dosyayaEkle} disabled={yukleniyor}>
                  {f.dosya && !dosyaId ? `Föy ${f.dosya.foyNo} belgelerine ekle` : 'Dosyanın belgelerine ekle'}
                </button>
              ) : (
                <details className="ah-detay" onToggle={(e) => { if (e.currentTarget.open) dosyalariGetir(); }}>
                  <summary>Bir dosyanın belgelerine ekle</summary>
                  <input className="ah-inp" placeholder="Föy no veya müvekkil ara" value={dosyaAra} onChange={(e) => setDosyaAra(e.target.value)} />
                  <select className="ah-inp" size={5} value={secilenDosyaId} onChange={(e) => setSecilenDosyaId(e.target.value)}>
                    {filtreliDosyalar.map((d) => (
                      <option key={d.Id ?? d.id} value={d.Id ?? d.id}>{d.FoyNo ?? d.foyNo} · {d.Muvekkil ?? d.muvekkil}</option>
                    ))}
                  </select>
                  <button type="button" className="ah-btn" onClick={dosyayaEkle} disabled={yukleniyor || !secilenDosyaId}>Seçili dosyaya ekle</button>
                </details>
              )}
              <button type="button" className="ah-btn ah-btn-sade" onClick={temizle}>Formu temizle</button>
              {durum && <p className={`ah-durum ah-durum-${durum.tip}`}>{durum.metin}</p>}
            </div>
          </div>

          <div className="ah-kart ah-dayanak">
            <button type="button" className="ah-acilir" aria-expanded={dayanakAcik} onClick={() => setDayanakAcik(!dayanakAcik)}>
              Dayanaklar ve hesap esasları
              <span className="ah-ok" aria-hidden="true">{dayanakAcik ? '−' : '+'}</span>
            </button>
            {dayanakAcik && (
              <ul className="ah-dayanak-liste">
                {DAYANAKLAR.map((d) => (
                  <li key={d.baslik}>
                    <b>{d.baslik}</b>
                    <span>{d.metin}</span>
                    {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer">Kaynağı aç</a>}
                  </li>
                ))}
                <li><b>Not</b><span>Bu hesap bilgilendirme amaçlıdır; bilirkişi raporunun yerine geçmez. Rakamlar girilen bilgilere dayanır.</span></li>
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default AracHasarHesap;
